import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { usePermissions } from '~/entities/viewer'
import { Debt } from '~/shared/api'
import { debtTypeEnumLabels } from '~/shared/config/constants'
import { AsyncGrid, FetchRowsFn } from '~/shared/ui/AsyncGrid'
import { Button } from '~/shared/ui/Buttons'
import { PlusIcon } from '~/shared/ui/Icons'
import { columns } from './columns'
import { filters } from './filters'
import { GridRow } from './types'

export const ListDebts = () => {
  const { t } = useTranslation()
  const { canDebtsUpdate } = usePermissions()

  const fetchRows = useCallback<FetchRowsFn<GridRow>>(
    async (page, pageSize, filters = [], sort) => {
      let builder = Debt.limit(pageSize).with('car').with('driver')

      filters?.forEach(({ key, value }) => {
        if (key === 'driverExtId') {
          builder = builder.option('filter[driver][extId]', value)
          return
        }
        builder = builder.where(key, value)
      })

      sort
        ? (builder = builder.orderBy(sort))
        : (builder = builder.orderBy('-createdAt'))

      const response = await builder.get(page)
      const data = response.getData()
      const rows = data.map((debt: Debt) => {
        const sourceType = debt?.getSourceType()
        const sourceTitle = debt?.getSourceTitle()
        return {
          ...debt.getAttributes(),
          source: {
            url: debt?.getSourceLink(),
            title: sourceTitle && t(sourceTitle),
          },
          id: debt?.getApiId() as UniqueId,
          car: debt?.getCar()?.getPlateNumber(),
          carExtId: debt?.getCar()?.getExtId(),
          driver: debt?.getDriver()?.getFullName(),
          driverExtId: debt?.getDriver()?.getExtId(),
          sourceType: t(debtTypeEnumLabels[sourceType]),
        }
      })

      const httpClientResponse = response.getHttpClientResponse()
      const axiosResponse = httpClientResponse?.getUnderlying()
      const total = axiosResponse?.data?.meta?.page?.total || 0

      return {
        rows,
        total,
      }
    },
    [t],
  )

  return (
    <AsyncGrid<GridRow>
      gridKey='debts-list'
      fetchRows={fetchRows}
      columns={columns}
      filters={filters}
      title={t('debts')}
      headerExtra={
        <Button startDecorator={<PlusIcon />} to='new' show={canDebtsUpdate}>
          {t('button.add')}
        </Button>
      }
    />
  )
}
